import { NavLink, Outlet, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Users, Kanban, MessageSquareText, Calendar, FileCheck, GitBranch, Sparkles, ShieldAlert,
  HeartHandshake, Network, DatabaseZap, Bot, Settings, Building2, LogOut, ShieldCheck, Menu, X, Images, Link2, Layers, Receipt, Landmark, LayoutTemplate, ChevronLeft, ChevronRight, ChevronUp, UserRound, KeyRound, FileUp,
  ChevronDown, LayoutGrid, BookOpen, Target, Dna, Building, Database, SlidersHorizontal, ShieldUser, Globe, ListChecks, MessagesSquare, NotebookPen, Palette, Megaphone, HardDrive, Lock, Star,
} from "lucide-react";
import { LockStar } from "./LockStar";
import { Phone, Mail, Share2, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useReview, setReviewMode } from "../lib/review";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { ProfileDialog, PasswordDialog } from "./ProfileDialogs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { PinManager } from "./settings/Round2";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { toast } from "sonner";
import { CompassStar } from "./CompassStar";
import { useAuth } from "../lib/auth";
import { ROLE_LABEL, BACKEND_URL } from "../lib/api";
import { useBrand, BrandProvider } from "../lib/branding";
import { EntitySwitcher } from "./EntitySwitcher";
import { AccessProvider, useAccess } from "../lib/access";
import { useLocation } from "react-router-dom";
import { AnnouncementBar } from "./AnnouncementBar";

const GOLD = "#C2A462";

export const ACCOUNT_NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: CompassStar },
  { to: "/app/contacts", label: "Contacts", icon: Users, area: "contacts" },
  { to: "/app/pipelines", label: "Pipelines", icon: Kanban, area: "pipelines" },
  { to: "/app/conversations", label: "Conversations", icon: MessageSquareText, area: "conversations" },
  { to: "/app/calendar", label: "Calendar", icon: Calendar, area: "calendar" },
  { to: "/app/essentials", label: "Document Essentials", icon: BookOpen },
  { to: "/app/forms", label: "Forms & Surveys", icon: FileCheck, area: "forms", group: "studio" },
  { to: "/app/workflows", label: "Workflows", icon: GitBranch, area: "workflows" },
  { to: "/app/funnels", label: "Funnels", icon: Sparkles, area: "funnels", group: "studio" },
  { to: "/app/websites", label: "Websites", icon: Globe, area: "funnels", group: "studio", adminOnly: true },
  { to: "/app/record-centre", label: "Record Centre", icon: ShieldAlert, area: "record_centre" },
  { to: "/app/counsellor", label: "Practice Suite", icon: HeartHandshake, area: "practice" },
  { to: "/app/integrations", label: "Integrations", icon: Network, area: "integrations" },
  { to: "/app/compliance", label: "AU Compliance", icon: DatabaseZap, area: "compliance" },
  { to: "/app/ai", label: "BOS AI", icon: Bot, area: "ai" },
  { to: "/app/assets", label: "Media Library", icon: Images, area: "assets", group: "studio" },
  { to: "/app/links", label: "Important Links", icon: Link2, area: "links" },
  { to: "/app/org-chart", label: "Org Chart", icon: Network, group: "dna" },
  { to: "/app/mission", label: "Mission & Values", icon: Target, group: "dna" },
  { to: "/app/dashboards", label: "Features Builder", icon: LayoutTemplate, adminOnly: true },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

const MANAGER_KEYS = ["dashboards", "settings", "contact-import", "widgets"];
const navKey = (to) => to.split("/")[2];
export function navAllowed(nav, key) {
  if (!nav || nav.lab || key === "essentials") return true;
  if (key === "org-chart") return !!nav.org_chart;
  if (key === "mission") return !!nav.mission;
  if (MANAGER_KEYS.includes(key)) return nav.manager;
  return !ACCOUNT_NAV.some((n) => navKey(n.to) === key) || nav.items.includes(key);
}

const AGENCY_NAV = [
  { to: "/agency", label: "Entity Accounts", icon: Building2, end: true, id: "entities" },
  { to: "/agency/plans", label: "Plans", icon: Layers, id: "plans" },
  { to: "/agency/billing", label: "Billing & Invoices", icon: Receipt, id: "billing" },
  { to: "/agency/details", label: "Enterprise Details", icon: Landmark, id: "details" },
  { to: "/agency/theme", label: "Global Settings", icon: Palette, id: "theme" },
  { to: "/agency/domains", label: "Custom Domains", icon: Globe, id: "domains" },
  { to: "/agency/comms", label: "Communication Centre", icon: Megaphone, id: "comms" },
  { to: "/agency/storage", label: "Storage Usage", icon: HardDrive, id: "storage" },
  { to: "/agency/security", label: "Security", icon: Lock, id: "security" },
  { to: "/agency/assets", label: "Media Library", icon: Images, id: "assets" },
];

const SIDEBAR_BG = "radial-gradient(ellipse at 20% 0%, #13213d 0%, #0b1428 45%, #070d1c 100%)";

function Logo({ collapsed }) {
  const b = useBrand();
  const src = b?.app_logo || b?.login_logo;
  return (
    <div className={`pt-6 pb-5 flex items-center justify-center gap-3 ${collapsed ? "px-2" : "px-5"}`} data-testid="sidebar-logo">
      {src ? <img src={`${BACKEND_URL}${src}`} alt="Logo" className="h-11 w-auto object-contain" /> : null}
      <div style={{ fontFamily: "'Cormorant Garamond', serif", color: GOLD }} className="text-[1.6rem] leading-none tracking-wide">{collapsed ? "B" : "BOS HQ"}</div>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, end, id, onNav, collapsed, indent }) {
  return (
    <NavLink to={to} end={end} onClick={onNav} title={collapsed ? label : undefined} data-testid={`nav-${id || to.split("/")[2]}`}
      className={({ isActive }) => `bos-nav-item relative flex items-center gap-3 rounded-[3px] py-2.5 ${collapsed ? "justify-center px-0" : indent ? "pl-6 pr-3" : "px-3"} text-[13px] tracking-wide transition-colors ${isActive ? "bg-[#C2A462]/[0.14] text-[#EBD6A0]" : "text-[#C9AD6E] hover:text-[#EBD6A0] hover:bg-[#C2A462]/[0.06]"}`}>
      {({ isActive }) => (<>
        {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full" style={{ background: GOLD }} />}
        <Icon className="h-4 w-4 opacity-90" strokeWidth={1.6} /> {!collapsed && label}
        {!collapsed && <LockStar id={`nav:${to}`} className="ml-auto" />}
      </>)}
    </NavLink>
  );
}

const STUDIO_SOON = [["Quizzes", ListChecks], ["Webchat", MessagesSquare], ["Blogs", NotebookPen]];

function SubLink({ to, label, icon: Icon, tab, onNav }) {
  const { pathname, search } = useLocation();
  const cur = new URLSearchParams(search).get("tab") || "business";
  const active = pathname === "/app/settings" && cur === tab;
  return (
    <Link to={to} onClick={onNav} data-testid={`nav-settings-${tab}`}
      className={`bos-nav-item relative flex items-center gap-3 rounded-[3px] py-2.5 px-3 text-[13px] tracking-wide transition-colors ${active ? "bg-[#C2A462]/[0.14] text-[#EBD6A0]" : "text-[#C9AD6E] hover:text-[#EBD6A0] hover:bg-[#C2A462]/[0.06]"}`}>
      {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full" style={{ background: GOLD }} />}
      <Icon className="h-4 w-4 opacity-90" strokeWidth={1.6} />{label}<LockStar id={`nav:settings-${tab}`} className="ml-auto" />
    </Link>
  );
}

function NavGroup({ id, title, icon: Icon, items, onNav, collapsed, children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const active = (items || []).some((n) => pathname === n.to || pathname.startsWith(`${n.to}/`)) || (id === "settings" && pathname === "/app/settings");
  const close = () => { setOpen(false); onNav?.(); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button title={collapsed ? title : undefined} className={`relative flex w-full items-center gap-3 rounded-[3px] py-2.5 text-left text-[13px] tracking-wide transition-colors ${collapsed ? "justify-center px-0" : "px-3"} ${open || active ? "bg-[#C2A462]/[0.10] text-[#EBD6A0]" : "text-[#C9AD6E] hover:bg-[#C2A462]/[0.06] hover:text-[#EBD6A0]"}`} data-testid={`nav-group-${id}-toggle`}>
          {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full" style={{ background: GOLD }} />}
          <Icon className="h-4 w-4 opacity-90" strokeWidth={1.6} />
          {!collapsed && <><span className="flex-1" data-testid={`nav-group-${id}-title`}>{title}</span>
            <LockStar id={`nav:${id}-group`} />
            <ChevronRight className="h-4 w-4 text-[#C2A462]/80" /></>}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={14} collisionPadding={12}
        className="w-64 rounded-xl border border-[#C2A462]/25 bg-[#0B1424] p-2 shadow-[0_12px_40px_rgba(0,0,0,0.55)] animate-in fade-in-0 slide-in-from-left-2 duration-150" data-testid={`nav-group-${id}`}>
        <div className="mb-1 border-b border-[#C2A462]/15 px-3 pb-2 pt-1 text-[12px] tracking-wide text-slate-400" data-testid={`nav-flyout-${id}-title`}>{title}</div>
        <div className="space-y-0.5" onClick={close}>{(items || []).map((n) => <NavItem key={n.to} {...n} />)}{children}</div>
      </PopoverContent>
    </Popover>
  );
}

function PresenceGroup({ onNav, collapsed }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    const load = () => api.get("/presence").then((r) => setP(r.data)).catch(() => setP(null));
    load();
    window.addEventListener("bos-presence-updated", load);
    return () => window.removeEventListener("bos-presence-updated", load);
  }, []);
  if (!p) return null;
  const sites = p.websites.filter((s) => s.ours).map((s) => ({ to: `/app/presence/site/${s.id}`, label: s.name || s.url, icon: Globe, id: `presence-site-${s.id}` }));
  const others = [...p.websites.filter((s) => !s.ours).map((s) => ({ id: s.id, url: s.url, label: s.name || s.url, I: Globe })), ...p.socials.map((s) => ({ id: s.id, url: s.url, label: s.label || s.platform, I: Share2 }))];
  const items = [{ to: "/app/presence", label: "Overview", icon: Share2, end: true, id: "presence-overview" }, ...sites];
  const ext = "relative flex items-center gap-3 rounded-[3px] py-2.5 px-3 text-[13px] tracking-wide text-[#C9AD6E] transition-colors hover:bg-[#C2A462]/[0.06] hover:text-[#EBD6A0]";
  return (
    <NavGroup id="presence" title="Presence" icon={Globe} items={items} onNav={onNav} collapsed={collapsed}>
      {others.map((s) => <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className={ext} data-testid={`nav-presence-social-${s.id}`}><s.I className="h-4 w-4 opacity-90" strokeWidth={1.6} /><span className="truncate">{s.label}</span><ExternalLink className="ml-auto h-3.5 w-3.5 opacity-60" /></a>)}
    </NavGroup>
  );
}

const SETTINGS_SUB = [["business", "Business Settings", Building], ["import", "Data", Database], ["features", "Feature Builder", LayoutTemplate], ["permissions", "Permissions", ShieldUser], ["system", "System", SlidersHorizontal]];

function Sidebar({ mode, onNav, collapsed }) {
  const { user } = useAuth();
  const { ready, can, nav } = useAccess();
  const items = mode === "agency" ? AGENCY_NAV : !ready ? [] : ACCOUNT_NAV
    .filter((n) => nav.lab ? !(n.adminOnly && user.role === "staff") : navAllowed(nav, navKey(n.to)))
    .filter((n) => !n.area || can(n.area))
    .filter((n) => nav?.lab || n.to !== "/app/dashboards")
    .map((n) => (n.to === "/app/dashboards" && nav.lab ? { ...n, label: "Features Lab" } : n));
  const studio = items.filter((x) => x.group === "studio");
  const dna = items.filter((x) => x.group === "dna");
  const top = items.filter((n) => !n.group);
  const ord = (n) => (n.to === "/app/dashboard" ? 0 : nav?.lab && n.to === "/app/dashboards" ? 1 : 2);
  if (mode !== "agency") top.sort((a, b) => ord(a) - ord(b) || a.label.localeCompare(b.label));
  const settings = mode !== "agency" && top.find((n) => n.to === "/app/settings");
  const list = settings ? top.filter((n) => n !== settings) : top;
  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 pb-3 border-b border-[#C2A462]/10" data-testid="sidebar-top-pinned">
        <Logo collapsed={collapsed} />
        {!collapsed && <EntitySwitcher mode={mode} />}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden" data-testid="sidebar-scroll">
      {!collapsed && <div className="mt-4 mb-2 px-6 text-[11px] tracking-wide text-slate-500">{mode === "agency" ? "Core settings" : "Workspace"}</div>}
      <nav className={`pb-3 space-y-0.5 ${collapsed ? "px-2 mt-2" : "px-3"}`}>
        {list.map((n) => <NavItem key={n.to} {...n} onNav={onNav} collapsed={collapsed} />)}
        {dna.length > 0 && <NavGroup id="dna" title="Organisation DNA" icon={Dna} items={dna} onNav={onNav} collapsed={collapsed} />}
        {mode !== "agency" && nav?.presence && <PresenceGroup onNav={onNav} collapsed={collapsed} />}
      </nav>
      {(studio.length > 0 || settings) && <div className={`pb-3 ${collapsed ? "px-2" : "px-3"}`} data-testid="sidebar-settings-pinned">
        <div className="mx-auto mb-2 h-px w-2/5 bg-[#C2A462]/60" data-testid="sidebar-gold-divider" />
        {studio.length > 0 && <NavGroup id="studio" title="Creative Studio" icon={LayoutGrid} items={studio} onNav={onNav} collapsed={collapsed}>
          {nav?.lab && STUDIO_SOON.map(([l, I]) => <div key={l} className="flex items-center gap-3 py-2 px-3 text-[13px] text-slate-500" data-testid={`nav-soon-${l.toLowerCase()}`}><I className="h-4 w-4" strokeWidth={1.6} />{l}<span className="ml-auto text-[10px] italic">soon</span></div>)}
        </NavGroup>}
        {settings && <NavGroup id="settings" title="Settings" icon={Settings} onNav={onNav} collapsed={collapsed}>
          {SETTINGS_SUB.filter(([t]) => !(t === "features" && nav?.lab) && !(["import", "features"].includes(t) && user.role === "staff")).map(([t, l, I]) => <SubLink key={t} to={`/app/settings?tab=${t}`} tab={t} label={l} icon={I} />)}
        </NavGroup>}
      </div>}
      </div>
      {mode !== "agency" && !collapsed && <SupportPanel />}
      <UserBlock collapsed={collapsed} />
      <div className={`py-2 border-t border-[#C2A462]/15 text-[11px] tracking-wide text-slate-500 flex items-center gap-2 ${collapsed ? "justify-center" : "px-6"}`} data-testid="data-sovereignty-badge">
        <ShieldCheck className="h-3.5 w-3.5 text-[#C2A462]/70" /> {!collapsed && "AUD · GST"}
      </div>
    </div>
  );
}

function SupportPanel() {
  const [s, setS] = useState(null);
  useEffect(() => {
    const load = () => api.get("/support-contact").then((r) => setS(r.data)).catch(() => setS(null));
    load();
    window.addEventListener("bos-support-updated", load);
    return () => window.removeEventListener("bos-support-updated", load);
  }, []);
  if (!s || !(s.support_phone || s.support_email)) return null;
  const row = "flex w-full items-center gap-3 py-0.5 text-left text-[13px] text-slate-200 hover:text-[#EBD6A0] transition-colors";
  const copy = (v, what) => navigator.clipboard?.writeText(v).then(() => toast.success(`${what} copied`), () => {});
  return (
    <div className="border-t border-[#C2A462]/15 px-6 py-2" data-testid="sidebar-support">
      <div className="mb-0.5 text-[11px] tracking-wide text-slate-500">Our support</div>
      {s.support_phone && <button type="button" onClick={() => copy(s.support_phone, "Phone number")} title="Click to copy" className={row} data-testid="sidebar-support-phone"><Phone className="h-4 w-4 shrink-0 text-[#C2A462]" strokeWidth={1.6} />{s.support_phone}</button>}
      {s.support_email && <button type="button" onClick={() => copy(s.support_email, "Email")} title="Click to copy" className={`${row} break-all`} data-testid="sidebar-support-email"><Mail className="h-4 w-4 shrink-0 text-[#C2A462]" strokeWidth={1.6} />{s.support_email}</button>}
    </div>
  );
}

function UserBlock({ collapsed }) {
  const { user, logout } = useAuth();
  const review = useReview();
  const [dlg, setDlg] = useState(null);
  const avatar = <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#C2A462]/50 text-[#C2A462] text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{user.name?.[0]}</span>;
  const out = <button onClick={logout} className="text-slate-500 hover:text-[#C2A462] transition-colors" title="Sign out" data-testid="logout-btn"><LogOut className="h-4 w-4" /></button>;
  const item = "flex items-center gap-2.5 rounded-[3px] px-3 py-2 text-[13px] text-[#C9AD6E] hover:bg-[#C2A462]/10 hover:text-[#EBD6A0] cursor-pointer outline-none";
  const menu = (trigger) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56 border-[#C2A462]/40 bg-[#0b1428] p-1.5" data-testid="profile-menu">
        <div className="px-3 py-2 border-b border-[#C2A462]/15 mb-1"><div className="text-[13px] text-slate-200 truncate">{user.name}</div><div className="text-[11px] text-slate-500 truncate">{user.email}</div></div>
        <DropdownMenuItem className={item} onSelect={() => setDlg("profile")} data-testid="profile-menu-my-profile"><UserRound className="h-4 w-4" />My profile</DropdownMenuItem>
        <DropdownMenuItem className={item} onSelect={() => setDlg("password")} data-testid="profile-menu-change-password"><KeyRound className="h-4 w-4" />Change password</DropdownMenuItem>
        {user.role !== "staff" && <DropdownMenuItem className={item} onSelect={() => setDlg("pin")} data-testid="profile-menu-pin"><Lock className="h-4 w-4" />Business Profile PIN</DropdownMenuItem>}
        {user.role === "agency_owner" && <DropdownMenuItem className={item} onSelect={() => setReviewMode(!review.on)} data-testid="profile-menu-review-mode"><Star className="h-4 w-4" />Review mode<span className={`ml-auto text-[10px] uppercase tracking-wider ${review.on ? "text-[#EBD6A0]" : "text-slate-500"}`} data-testid="review-mode-state">{review.on ? "On" : "Off"}</span></DropdownMenuItem>}
        <DropdownMenuItem className={item} onSelect={logout} data-testid="profile-menu-sign-out"><LogOut className="h-4 w-4" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
  const dialogs = <>{dlg === "profile" && <ProfileDialog onClose={() => setDlg(null)} />}{dlg === "password" && <PasswordDialog onClose={() => setDlg(null)} />}
    {dlg === "pin" && <Dialog open onOpenChange={(o) => !o && setDlg(null)}><DialogContent data-testid="pin-dialog"><DialogHeader><DialogTitle>Business Profile & Billing PIN</DialogTitle></DialogHeader>
      <p className="text-xs text-muted-foreground">This 4-digit PIN protects the Business Profile & Billing tab for the entity you're in. Only the owner or entity admin can set it.</p><PinManager /></DialogContent></Dialog>}</>;
  if (collapsed) return <div className="border-t border-[#C2A462]/15 py-4 flex flex-col items-center gap-3" data-testid="sidebar-user">{menu(<button title="My account" data-testid="profile-menu-btn">{avatar}</button>)}{out}{dialogs}</div>;
  return (
    <div className="border-t border-[#C2A462]/15 px-3 py-1.5 flex items-center gap-2" data-testid="sidebar-user">
      {menu(
        <button className="flex flex-1 min-w-0 items-center gap-3 rounded-[3px] px-2 py-1.5 text-left transition-colors hover:bg-[#C2A462]/[0.08]" data-testid="profile-menu-btn">
          {avatar}
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-slate-200 leading-tight truncate" data-testid="current-user-name">{user.name}</div>
            <div className="text-[11px] tracking-wide text-[#C2A462]/80 truncate">{ROLE_LABEL[user.role]}</div>
          </div>
          <ChevronUp className="h-3.5 w-3.5 text-slate-500" />
        </button>
      )}
      {out}
      {dialogs}
    </div>
  );
}

function TopBar({ onMenu }) {
  return (
    <header className="lg:hidden sticky top-0 z-30 h-14 border-b border-white/[0.06] bg-black/80 backdrop-blur-xl flex items-center gap-3 px-5">
      <button className="text-[#C2A462]" onClick={onMenu} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
      <span className="text-[#C2A462] text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>BOS HQ</span>
    </header>
  );
}

function AreaGuard({ children }) {
  const { pathname } = useLocation();
  const { ready, can, nav } = useAccess();
  const key = pathname.startsWith("/app/forms/fill/") ? "essentials" : pathname.split("/")[2];
  if (ready && !navAllowed(nav, key)) return <NoAccess label={ACCOUNT_NAV.find((n) => navKey(n.to) === key)?.label || "This page"} />;
  if (key === "essentials") return children;
  const item = ACCOUNT_NAV.find((n) => n.area && pathname.startsWith(n.to));
  if (!item) return children;
  if (!ready) return null;
  if (can(item.area)) return children;
  return <NoAccess label={item.label} />;
}

function NoAccess({ label }) {
  return (
    <div className="max-w-lg mt-16 rounded-md border border-[#C2A462]/30 bg-card p-6" data-testid="no-access-panel">
      <div className="text-[#C2A462] font-semibold mb-1">{label} isn't available</div>
      <p className="text-sm text-muted-foreground">This area is switched off for this entity, or your access profile doesn't include it. Ask your administrator if you need access.</p>
      <NavLink to="/app/dashboard" className="inline-block mt-4 text-sm text-[#C2A462] hover:underline" data-testid="no-access-back-link">Back to dashboard</NavLink>
    </div>
  );
}

export default function AppShell(props) {
  return <AccessProvider><BrandProvider><Shell {...props} /></BrandProvider></AccessProvider>;
}

function Shell({ mode = "account" }) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(localStorage.getItem("bos_sidebar") === "collapsed");
  const toggle = () => { localStorage.setItem("bos_sidebar", collapsed ? "open" : "collapsed"); setCollapsed(!collapsed); };
  const { accountId } = useAuth();
  return (
    <div className="min-h-screen bg-black">
      <aside className={`bos-sidebar hidden lg:block fixed inset-y-0 left-0 border-r border-[#C2A462]/50 z-40 transition-[width] duration-300 ${collapsed ? "w-16" : "w-64"}`} style={{ background: SIDEBAR_BG }} data-testid="sidebar">
        <Sidebar mode={mode} collapsed={collapsed} />
        <button onClick={toggle} title={collapsed ? "Expand sidebar" : "Collapse sidebar"} data-testid="sidebar-collapse-btn"
          className="absolute top-1/2 -right-4 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full border border-[#C2A462]/70 bg-[#0b1428] text-[#C2A462] shadow-lg transition-[background-color,transform] hover:bg-[#C2A462] hover:text-[#0b1428] hover:scale-105">
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 bg-black/70 lg:hidden" onClick={() => setOpen(false)}>
          <aside className="bos-sidebar w-72 h-full border-r border-[#C2A462]/50 relative" style={{ background: SIDEBAR_BG }} onClick={(e) => e.stopPropagation()}>
            <button className="absolute right-3 top-3 text-slate-400" onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X className="h-4 w-4" /></button>
            <Sidebar mode={mode} onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className={`transition-[padding] duration-300 ${collapsed ? "lg:pl-16" : "lg:pl-64"}`}>
        <TopBar onMenu={() => setOpen(true)} />
        <main className="p-5 sm:p-8 fade-up">{mode === "account" ? <AreaGuard><AnnouncementBar key={`ann-${accountId}`} /><Outlet key={accountId} /></AreaGuard> : <Outlet key="agency" />}</main>
      </div>
    </div>
  );
}
